var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useState } from 'react';
import { createFile } from './useCreateFile';
import { getPresignedUrl } from './useGetPresignedUrl';
// upload and create multiple files
export function useFileUploader(folderPath = 'documents') {
    const [items, setItems] = useState([]);
    const updateState = useCallback((file, value) => {
        setItems(items => {
            const index = items.findIndex(item => item.file === file);
            if (index < 0) {
                return items;
            }
            const copy = Array.from(items);
            const item = copy[index];
            const newValue = typeof value === 'function' ? value(item) : value;
            copy.splice(index, 1, Object.assign(Object.assign({}, item), newValue));
            return copy;
        });
    }, []);
    const handlePresign = useCallback((file) => __awaiter(this, void 0, void 0, function* () {
        updateState(file, { status: 'loading' });
        const presign = yield getPresignedUrl(folderPath, file.type);
        updateState(file, presign.data
            ? { progress: 1, presignedUrl: presign.data }
            : { status: 'presignError', error: presign.error });
        return presign.data;
    }), [folderPath, updateState]);
    const handleUpload = useCallback((file, presignedUrl) => {
        const request = new XMLHttpRequest();
        updateState(file, { abort: () => request.abort() });
        return new Promise(resolve => {
            const handleErrorEvent = () => {
                updateState(file, {
                    status: 'uploadError',
                    error: {
                        statusCode: 500,
                        error: 'Upload Error',
                        message: 'An error occurred while uploading the file.'
                    }
                });
                resolve(false);
            };
            request.addEventListener('error', handleErrorEvent);
            request.addEventListener('timeout', handleErrorEvent);
            request.upload.addEventListener('error', handleErrorEvent);
            request.upload.addEventListener('progress', e => {
                if (e.lengthComputable) {
                    updateState(file, {
                        status: 'uploading',
                        progress: (e.loaded / e.total) * 100
                    });
                }
            });
            request.upload.addEventListener('load', () => {
                updateState(file, {
                    progress: 100,
                    status: 'uploaded',
                    response: request.response
                });
                resolve(true);
            });
            request.open('PUT', presignedUrl.signedUrl);
            // request.timeout = 5000;
            request.setRequestHeader('Content-Type', file.type);
            request.responseType = 'text';
            request.send(file);
        });
    }, [updateState]);
    // create file dto
    const handleCreate = useCallback((file, dto) => __awaiter(this, void 0, void 0, function* () {
        updateState(file, { status: 'creating' });
        const { data, error } = yield createFile(dto);
        updateState(file, data
            ? { fileDto: data, status: 'created' }
            : { error, status: 'createError' });
    }), [updateState]);
    // handle presigned url, upload, and create file
    const handleFile = useCallback((file, createDto) => __awaiter(this, void 0, void 0, function* () {
        const presignedUrl = yield handlePresign(file);
        if (!presignedUrl) {
            return;
        }
        const didUpload = yield handleUpload(file, presignedUrl);
        if (didUpload) {
            const dto = createDto(file, presignedUrl);
            yield handleCreate(file, dto);
        }
    }), [handleCreate, handlePresign, handleUpload]);
    const upload = useCallback((files, createDto) => {
        const newItems = [];
        for (const file of files) {
            // filter existing files
            if (!items.some(item => item.file === file)) {
                newItems.push({ file, status: 'init', progress: 0 });
            }
        }
        setItems(items => items.concat(newItems));
        newItems.forEach(item => handleFile(item.file, createDto));
    }, [items, handleFile]);
    const cancel = useCallback((file) => {
        updateState(file, item => {
            var _a;
            (_a = item.abort) === null || _a === void 0 ? void 0 : _a.call(item);
            return { progress: 0, error: undefined, status: 'cancelled' };
        });
    }, [updateState]);
    const remove = useCallback((file) => {
        setItems(items => {
            var _a;
            const index = items.findIndex(item => item.file === file);
            if (index < 0) {
                return items;
            }
            const copy = Array.from(items);
            const item = copy[index];
            (_a = item.abort) === null || _a === void 0 ? void 0 : _a.call(item);
            copy.splice(index, 1);
            return copy;
        });
    }, []);
    const clear = useCallback((skipCreated = false) => {
        setItems(items => {
            return items.filter(item => {
                var _a;
                if (skipCreated && item.fileDto) {
                    return true;
                }
                (_a = item.abort) === null || _a === void 0 ? void 0 : _a.call(item);
                return false;
            });
        });
    }, []);
    const isUploaded = useCallback((file) => {
        return items.some(item => {
            return (item.file === file && ['created', 'uploaded'].includes(item.status));
        });
    }, [items]);
    const find = useCallback((file) => items.find(item => item.file === file), [items]);
    const uploader = useMemo(() => {
        return { items, cancel, clear, find, isUploaded, remove, setItems, upload };
    }, [items, cancel, clear, find, isUploaded, remove, upload]);
    return uploader;
}
//# sourceMappingURL=useFileUploader.js.map